import React, { useCallback, useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import MiniBarChart from "./MiniBarChart";
import { METRICS, sel } from "./configuratorMetrics";

const dayIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const GRAN = [["1", "Cate 1 ora (24)"], ["3", "Cate 3 ore (8)"], ["6", "Cate 6 ore (4)"], ["12", "Cate 12 ore (2)"]];

function CumulatTable({ data }) {
  const labels = data.labels || [];
  const val = (k, i) => data.series?.[k]?.[i] ?? 0;
  const total = (k) => labels.reduce((a, _, i) => a + val(k, i), 0);
  return (
    <div data-testid="cumulat-table" className="mb-6 overflow-x-auto rounded border border-slate-200 bg-white p-3 shadow-sm">
      <h3 className="mb-2 text-sm font-semibold text-slate-700">Tabel cumulat (ora Bucuresti) - {labels.length} randuri</h3>
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="bg-slate-100">
            <th className="px-2 py-1.5 text-left font-semibold">Ora</th>
            {METRICS.map(([k, l, c]) => <th key={k} className="whitespace-nowrap px-2 py-1.5 text-right font-semibold" style={{ color: c }}>{l}</th>)}
          </tr>
        </thead>
        <tbody>
          {labels.map((lab, i) => (
            <tr key={lab} className="border-b border-slate-100 hover:bg-slate-50">
              <td className="px-2 py-1 font-mono font-semibold">{lab}</td>
              {METRICS.map(([k]) => <td key={k} className="px-2 py-1 text-right">{val(k, i)}</td>)}
            </tr>
          ))}
          <tr className="bg-slate-200 font-bold">
            <td className="px-2 py-1.5">TOTAL</td>
            {METRICS.map(([k]) => <td key={k} data-testid={`cumulat-total-${k}`} className="px-2 py-1.5 text-right">{total(k)}</td>)}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function CumulatPeOre() {
  const [f, setF] = useState({ date_from: dayIso(6), date_to: dayIso(0), granularitate: "1", country: "all" });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!f.date_from || !f.date_to) return;
    setLoading(true);
    setError("");
    setData(null);
    try {
      const { data } = await api.get("/api/configurator/cumulat-pe-ore", { params: f });
      setData(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Eroare la incarcare");
    } finally {
      setLoading(false);
    }
  }, [f]);

  useEffect(() => { load(); }, [load]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const { data: blob } = await api.get("/api/configurator/cumulat-pe-ore/export", { params: f, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cumulat_pe_ore_${f.date_from}_${f.date_to}_${f.granularitate}h_${f.country}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError("Export esuat");
    } finally {
      setExporting(false);
    }
  };

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const countries = data?.available_countries || [];

  return (
    <div data-testid="cumulat-pe-ore">
      <div className="mb-5 flex flex-wrap items-center justify-end gap-3 text-sm">
        <label className="flex items-center gap-2">De la
          <input data-testid="cumulat-date-from" type="date" className={sel} value={f.date_from} max={f.date_to} onChange={set("date_from")} />
        </label>
        <label className="flex items-center gap-2">Pana la
          <input data-testid="cumulat-date-to" type="date" className={sel} value={f.date_to} min={f.date_from} max={dayIso(0)} onChange={set("date_to")} />
        </label>
        <label className="flex items-center gap-2">Granularitate
          <select data-testid="cumulat-granularitate" className={sel} value={f.granularitate} onChange={set("granularitate")}>
            {GRAN.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2">Tara
          <select data-testid="cumulat-country" className={sel} value={f.country} onChange={set("country")}>
            <option value="all">Toate</option>
            <option value="RO">Romania</option>
            <option value="EXT">Extern</option>
            <option value="UNK">Necunoscut</option>
            {countries.filter((c) => c && c !== "RO").map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        {data?.total_zile ? <span data-testid="cumulat-total-zile" className="text-xs text-slate-500">{data.total_zile} zile</span> : null}
        <button data-testid="cumulat-export-btn" onClick={exportXlsx} disabled={exporting || loading || !data} className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50">
          <Download size={14} /> {exporting ? "Export..." : "Export Excel"}
        </button>
      </div>

      {loading && <div data-testid="cumulat-loading" className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Se incarca...</div>}
      {error && <div data-testid="cumulat-error" className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {data && (
        <>
          <CumulatTable data={data} />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {METRICS.map(([key, label, color]) => {
              const values = (data.labels || []).map((_, i) => data.series?.[key]?.[i] ?? 0);
              return (
                <div key={key} data-testid={`cumulat-chart-${key}`} className="rounded border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-700">{label}</span>
                    <span className="text-xs text-slate-500">Total: <b className="text-slate-800">{values.reduce((a, b) => a + b, 0)}</b></span>
                  </div>
                  <MiniBarChart labels={data.labels || []} values={values} color={color} />
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
